import axios from "axios";
import { Dispatch, SetStateAction } from "react";
import { images } from "../assets/images";
import { CandidateProfile } from "../models/profile";
import { BaseResponse } from "../models/base";
import { PreSignedUrlData } from "../models/master";

export const saveCompanyName = async (
  setLoading: Dispatch<SetStateAction<boolean>>,
  companyName: string
): Promise<void> => {
  try {
    const response = await axios.post(
      `${
        import.meta.env.VITE_BASE_URL
      }/clients/company/profile/update/${localStorage.getItem("companyId")}`,

      {
        companyId: localStorage.getItem("companyId"),
        companyName: companyName,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    if (response.data.statusCode === "OK") {
      localStorage.setItem("companyName", response.data.data.companyName);
    } else {
    }
  } catch (error) {
    console.error("Error during company name change:", error);
  } finally {
    setLoading(false);
  }
};

export const editProfileApi = async (firstname: string): Promise<void> => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/clients/user/edit`,

      {
        firstname: firstname,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    if (response.data.statusCode === "OK") {
      localStorage.setItem("companyName", response.data.data.companyName);
    } else {
    }
  } catch (error) {
    console.error("Error during edit profile:", error);
  }
};

export const editCompanyProfileApi = async (
  userName: string,
  contact: string,
  countryCode: string,
  description: string,
  firstName: string,
  email: string,
  companyLogo?: string
): Promise<BaseResponse<boolean> | undefined> => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/clients/company/profile/update/client`,

      {
        companyId: localStorage.getItem("companyId"),
        userName: userName,
        firstName: firstName,
        description: description,
        companyContact: contact,
        countryCode: countryCode,
        companyLogo: companyLogo,
        email: email,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during edit profile:", error);
  }
};

export const removeCompanyLogoApi = async (): Promise<
  BaseResponse<boolean> | undefined
> => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/clients/company/profile/update/client`,

      {
        companyId: localStorage.getItem("companyId"),
        userName: null,
        firstName: null,
        description: null,
        companyContact: null,
        countryCode: null,
        companyLogo: images.companyLogoUpdate,
        email: null,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during edit profile:", error);
  }
};

export const uploadLogoPresignedApi = async (
  file: File
): Promise<BaseResponse<PreSignedUrlData> | undefined> => {
  const fileExtension = file.name.split(".").pop();

  const contentType = file.type;

  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/clients/company/profile/get/presigned`,

      {
        fileExtension: fileExtension,
        contentType: contentType,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during edit profile:", error);
  }
};

/**
 * Upload company profile picture via form-data directly to /pic/update
 */
export const uploadCompanyProfilePictureApi = async (
  file: File
): Promise<BaseResponse<{ fileUrl?: string }> | undefined> => {
  try {
    const formData = new FormData();
    formData.append("picture", file);

    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/clients/company/profile/pic/update`,
      formData,
      {
        headers: {
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during picture upload:", error);
  }
};

export const getUserDetails = async (profileId: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_BASE_URL}/clients/user/profile/get/${profileId}`,

      {
        headers: {
          Authorization: localStorage.getItem("userId"),
        },
      }
    );

    if (response.data.statusCode === "OK") {
      return response.data;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error during getting user profile:", error);
    return false;
  }
};

export const getProfile = async (token?: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_BASE_URL}/clients/user/me`,

      {
        headers: {
          Authorization: token || localStorage.getItem("userId"),
        },
      }
    );

    if (response.data.statusCode === "OK") {
      localStorage.setItem("companyId", response.data.data.meta.companyId);
      return response.data;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error during getting user profile:", error);
    return false;
  }
};

export const getProfileInformation = async (
  profileId: string
): Promise<CandidateProfile | false> => {
  try {
    const response = await axios.get(
      `${
        import.meta.env.VITE_BASE_URL
      }/candidate/profile/profileId/get/${profileId}/${
        localStorage.getItem("companyId") || ""
      }`,
      {
        headers: {
          Authorization: localStorage.getItem("userId"),
        },
      }
    );

    if (response.data.statusCode === "OK") {
      return response.data.data.candidateProfile as CandidateProfile; // Cast the response data to CandidateProfile
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error during getting user profile:", error);
    return false;
  }
};

export const accessProfileApi = async (profileId: string): Promise<any> => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/candidate/profile/view`,
      {
        profileId: profileId,
        companyId: localStorage.getItem("companyId") || "",
      },
      {
        headers: {
          Authorization: localStorage.getItem("userId"), // Ensure `Token` is defined or passed as a parameter
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error during getting user profile:", error);
    return false;
  }
};

export const ctaAnalyticsApi = async (payload: {}) => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/candidate/analytics/add`,

      payload
    );

    if (response.data.statusCode === "OK") {
      return response.data;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error during getting user profile:", error);
    return false;
  }
};

export const refreshToken = async () => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/clients/user/refresh-token`,
      { token: localStorage.getItem("userId") },
      {
        headers: {
          Authorization: localStorage.getItem("userId"),
        },
      }
    );

    if (response.data.statusCode === "OK") {
      return response.data;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error during getting user profile:", error);
    return false;
  }
};

export const getIpAdress = async () => {
  try {
    const response = await axios.get(`https://api.ipify.org?format=json`);
    localStorage.setItem("ipAddress", response.data.ip);
  } catch (error) {
    console.error("Error during getting user profile:", error);
    return false;
  }
};
